import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, } from '@angular/core';
import { defer as _defer, isNil as _isNil } from 'lodash';
import { Keyboard } from '@blcloud/bl-common';

/**
 * 検索条件入力パネル
 *
 * 常時表示される基本項目表示部と、「＋」ボタンで展開される詳細項目表示を外部から挿入して使用します。
 */
@Component({
  selector: 'app-sns-send-message-condition-panel',
  templateUrl: './sns-send-message-condition-panel.component.html',
  styleUrls: ['./sns-send-message-condition-panel.component.scss']
})
export class SnsSendMessageConditionPanelComponent implements OnInit {

  /** 表示文言:(条件取消) */
  @Input() clearBtnText = '条件取消';

  /** 表示文言:(閉じる)) */
  @Input() searchBtnText = '閉じる';

  /** 表示文言:(詳細ボタン) */
  @Input() detailToggleBtnText = '詳細';

  /** 表示文言:(詳細パネルのタイトル) */
  @Input() detailPanelText = '詳細条件';

  /** 詳細パネルを自動で閉じるか */
  @Input() autoClose = true;

  /** 詳細以外をクリックして自動で閉じるか */
  @Input() closeBackdrop = true;

  /** 詳細ボタンを表示するか */
  @Input() isDisplayDetailToggle = true;

  /** 詳細パネルの検索ボタンを表示するか */
  @Input() isDisplaySubSearchButton = false;

  /** 詳細ボタン無効フラグ */
  @Input() disabledDetailToggle = false;

  /** 検索ボタン無効フラグ */
  @Input() disabledSearchButton = false;

  /** 検索ボタンツールチップ */
  @Input() searchButtonTooltip = '';

  @Output() toggleDetailEvent = new EventEmitter();

  @Output() searchEvent = new EventEmitter();

  @Output() clearEvent = new EventEmitter();

  @Output() shownEvent = new EventEmitter();

  @Output() hiddenEvent = new EventEmitter();

  /**
   * 詳細パネル表示状態
   */
  private _detailConditionShown = false;
  get detailConditionShown() {
    return this._detailConditionShown;
  }

  /**
   * アニメーション中
   */
  private _inTransition = false;
  get inTransition() {
    return this._inTransition;
  }

  @ViewChild('btnDetail') btnDetail: ElementRef;
  @ViewChild('btnClear') btnCancel: ElementRef;
  @ViewChild('basicPanel') basicPanel: ElementRef;

  @ViewChild('basicSearchBtn') basicSearchBtn: ElementRef;
  @ViewChild('detailSearchBtn') detailSearchBtn: ElementRef;
  basicWidth = '';

  constructor() {
  }

  ngOnInit(): void {
    _defer(_ => {
      this.basicWidth = window.getComputedStyle(this.basicPanel.nativeElement).width;
    });
  }

  /**
   * 条件取消ボタン押下処理
   *
   * @param event ボタンイベント
   */
  onClear(event): void {
    this.clearEvent.emit(event);
    if (this.autoClose && this._detailConditionShown) {
      this.onClickDetailToggle(event);
    }
  }

  /**
   * 検索ボタン押下処理
   *
   * @param event ボタンイベント
   */
  onSearch(event): void {
    this.searchEvent.emit(event);
    if (this.autoClose && this._detailConditionShown) {
      this.onClickDetailToggle(event);
    }
  }

  /**
   * ＋/－ボタン押下処理
   * @param event クリックイベント
   */
  onClickDetailToggle(event): void {
    this._detailConditionShown = !this._detailConditionShown;
    this._inTransition = true;
    this.toggleDetailEvent.emit(event);
    this.emitEvent();
  }

  /**
   * 検索条件入力パネル外クリック処理
   * @param event クリックイベント
   */
  onClickBackdrop(event): void {
    if (this._detailConditionShown) {
      this.onClickDetailToggle(event);
    }
  }

  /**
   * パネルのアニメーション完了
   * @param event
   */
  onTransitionEnd(event): void {
    if (event.propertyName === 'visibility') {
      _defer(() => { this._inTransition = false; });
    }
  }

  /**
   * パネルを表示する
   */
  show() {
    if (this._detailConditionShown) {
      // すでに表示されている場合は、何もしない
      return;
    }

    this._detailConditionShown = true;
    this._inTransition = true;
    this.emitEvent();
  }

  /**
   * パネルを閉じる
   * @param el フォーカスを当てたい要素
   */
  hide(el?: HTMLElement) {
    if (!this._detailConditionShown) {
      // すでに非表示の場合は、何もしない
      return;
    }

    try {
      if (el) {
        el.focus();
      } else {
        this.btnDetail.nativeElement.focus();
      }
    } catch (_err) {
      // 部品の実装によっては changedetection でエラーが発生するので
      // 明示的にフォーカスを外し、値を確定させた後で画面を閉じる
    }

    this._detailConditionShown = false;
    this._inTransition = true;
    this.emitEvent();
  }

  /**
   * 基本条件の検索ボタンにフォーカスを付与します。
   */
  focusBasicSearchBtn() {
    if (!_isNil(this.basicSearchBtn)) {
      this.basicSearchBtn.nativeElement.focus();
    }
  }

  /**
   * 詳細条件の検索ボタンにフォーカスを付与します。
   */
  focusDetailSearchBtn() {
    if (!_isNil(this.detailSearchBtn)) {
      this.detailSearchBtn.nativeElement.focus();
    }
  }

  private emitEvent() {
    let eventEmitter: EventEmitter<any>;
    if (this._detailConditionShown) {
      eventEmitter = this.shownEvent;
    } else {
      eventEmitter = this.hiddenEvent;
    }
    setTimeout(() => {
      eventEmitter.emit();
    }, 200);
  }

  @HostListener('keydown.escape', ['$event'])
  onKeydown($event) {
    if (this._detailConditionShown) {
      $event.preventDefault();
      this.hide();
    }
  }

  /**
   * Alt + 下キーで詳細パネルを開く（閉じる）
   * @param $event
   */
  onKeyDown($event: KeyboardEvent) {
    const keyName = Keyboard.Keys[$event.keyCode];
    switch (keyName) {
      case 'down':
        // altと同時押し
        if ($event.altKey === true) {
          if (this._detailConditionShown) {
            this.hide();
          } else {
            this.show();
          }
          $event.preventDefault();
          $event.stopPropagation();
        }
        break;
      case 'esc':
        if (this._detailConditionShown) {
          this.hide();
          $event.preventDefault();
          $event.stopPropagation();
        }
        break;
      case 'enter':
        $event.preventDefault();
        break;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-modal/condition-panel/sns-send-message-condition-panel.component.ts